import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { LogoMark } from "../../components/Logo";

export default function HostedForm() {
  const { orgSlug, formSlug } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [values, setValues] = useState({});
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/forms/${orgSlug}/${formSlug}`)
      .then(({ data }) => setForm(data.form))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Form not found"));
  }, [orgSlug, formSlug]);

  const utm = () => {
    const u = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => { if (params.get(k)) u[k] = params.get(k); });
    return u;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/forms/${orgSlug}/${formSlug}`, { data: values, utm: utm() });
      if (data.redirect_url) { window.location.href = data.redirect_url; return; }
      setDone(data.message || "Thank you!");
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (error && !form) return <Shell embed={embed}><p className="text-center text-neutral-500 py-20" data-testid="hosted-form-error">{error}</p></Shell>;
  if (!form) return <Shell embed={embed}><p className="text-center text-neutral-400 py-20">Loading…</p></Shell>;

  return (
    <Shell embed={embed}>
      {!embed && (
        <div className="flex items-center gap-2.5 mb-6">
          <LogoMark size={30} mono="#000000" />
          <span className="font-display font-bold text-alto-black">{form.org_name}</span>
        </div>
      )}
      <h1 className="font-display text-2xl font-bold text-alto-black mb-6" data-testid="hosted-form-title">{form.name}</h1>
      {done ? (
        <div className="text-center py-10" data-testid="hosted-form-success">
          <div className="w-14 h-14 rounded-full bg-alto-red/10 flex items-center justify-center mx-auto mb-5"><Check className="text-alto-red" size={28} /></div>
          <p className="text-slate-700">{done}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="hosted-form">
          <input type="text" name="_hp" tabIndex={-1} autoComplete="off" onChange={(e) => setValues({ ...values, _hp: e.target.value })} className="hidden" aria-hidden />
          {form.fields.map((f) => (
            <FieldRenderer key={f.key} field={f} value={values[f.key] || ""} onChange={(v) => setValues({ ...values, [f.key]: v })} />
          ))}
          <button type="submit" disabled={submitting} data-testid="hosted-form-submit" className="btn-red w-full font-bold px-6 py-3.5 rounded-md text-sm disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </form>
      )}
    </Shell>
  );
}

function FieldRenderer({ field, value, onChange }) {
  const base = "w-full bg-white border border-slate-300 rounded-md px-4 py-3 text-sm text-slate-900 focus:border-alto-red outline-none";
  if (field.type === "textarea")
    return <Wrap field={field}><textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} required={field.required} data-testid={`field-${field.key}`} className={base + " resize-none"} /></Wrap>;
  if (field.type === "checkbox" || field.type === "consent")
    return <label className="flex items-center gap-3 text-sm text-slate-700"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} required={field.required} data-testid={`field-${field.key}`} className="w-4 h-4 accent-red-600" />{field.label}</label>;
  const type = field.type === "tel" ? "tel" : field.type === "email" ? "email" : field.type === "number" ? "number" : field.type === "date" ? "date" : "text";
  return <Wrap field={field}><input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={field.required} data-testid={`field-${field.key}`} className={base} /></Wrap>;
}

function Wrap({ field, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{field.label}{field.required && <span className="text-alto-red"> *</span>}</label>
      {children}
    </div>
  );
}

function Shell({ embed, children }) {
  return (
    <div className={embed ? "p-4 bg-white" : "min-h-screen bg-slate-100 flex items-center justify-center p-4"}>
      <div className={embed ? "w-full" : "w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8"}>{children}</div>
    </div>
  );
}
